// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'Tabs' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Tabs from '@wandb/weave/components/Tabs';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {urlPrefixed} from '../../config';
import * as Generated from '../../generated/graphql';
import {
  PreviewableLinkResourceType,
  useFetchPreviewableLinkQuery,
} from '../../generated/graphql';
import {useQuery} from '../../state/graphql/query';
import {useApolloClient, useSelector} from '../../state/hooks';
import {getReportPart} from '../../state/reports/selectors';
import {ReportViewRef} from '../../state/reports/types';
import {useViewer} from '../../state/viewer/hooks';
import {deleteView} from '../../state/views/api';
import {ViewPermissionsData} from '../../state/views/graphql';
import {LoadableView} from '../../state/views/types';
import {isOwnView} from '../../state/views/util';
import {viewerUsingAdminPrivileges} from '../../util/admin';
import {propagateErrorsContext} from '../../util/errors';
import {useIsMounted} from '../../util/hooks';
import {accessIsPublicOrOpen} from '../../util/permissions';
import {reportView} from '../../util/urls';
import {ShareReportModalHeaderAction} from './action';
import {CopyLinkSection} from './CopyLinkSection';
import {PreviewImageSection} from './PreviewImageSection';
import {ShareWithSection} from './SharedWithSection';
import {ShareInputSection} from './ShareInputSection';
import {ShareLinks} from './ShareLinks';
import * as S from './ShareReportTrigger.styles';
import {ShareReportTriggerContextProvider} from './ShareReportTriggerContextProvider';
import {
  firstPanelExportableAsImageFromViewPart,
  getPreviewImageFromURL,
  useMagicLinkFunctions,
} from './util';

interface ShareReportTriggerProps {
  children?: ReactNode;
  isModalInitiallyOpen?: boolean;
  isSharePanel?: boolean;
  modalHeaderAction?: ShareReportModalHeaderAction;
  modalTitle?: string;
  userIsMemberOfTeam?: boolean;
  userIsTeamAdmin?: boolean;
  viewRef: ReportViewRef;
  view: LoadableView;
  shouldShowInviteTab?: boolean;
  onModalClose?(): void;
  onTriggerContentClick?(): void;
}

export const ShareReportTrigger: FC<ShareReportTriggerProps> = ({
  children,
  isModalInitiallyOpen = false,
  modalHeaderAction = ShareReportModalHeaderAction.COPY_EMBEDED_CODE,
  modalTitle = 'Share Report',
  onModalClose,
  onTriggerContentClick,
  userIsMemberOfTeam,
  userIsTeamAdmin,
  viewRef,
  view,
  isSharePanel = false,
  shouldShowInviteTab = true,
}) => {
  const accessTokens = view.accessTokens ?? [];

  const viewer = useViewer();
  const accessQuery = useQuery<
    ViewPermissionsData,
    Generated.ViewPermissionsQueryVariables
  >(Generated.ViewPermissionsDocument, {
    variables: {
      projectName: view.project?.name ?? '',
      entityName: view.project?.entityName ?? '',
    },
    skip: view.project == null,
  });

  const [isModalOpen, setIsModalOpen] = useState(isModalInitiallyOpen);
  const [shouldDeleteView, setShouldDeleteView] = useState<boolean>(true);
  const client = useApolloClient();

  const deleteViewIfNotUsed = async () => {
    if (
      view.id &&
      isSharePanel &&
      accessTokens?.length === 0 &&
      shouldDeleteView
    ) {
      deleteView(client, view.id);
    }
  };

  const closeModal = () => {
    deleteViewIfNotUsed();
    onModalClose?.();
    setIsModalOpen(false);
  };

  const {
    data: {
      entity: {
        isTeam = true,
        settings: {disableMagicLinkSharing = true, hidden = true} = {},
      } = {},
    } = {},
  } = accessQuery.loading || !accessQuery.data?.entity ? {} : accessQuery;

  const {access = ''} =
    accessQuery.loading || !accessQuery.data?.project
      ? {}
      : accessQuery.data.project;

  const isPrivateAccess =
    ['USER_READ', 'USER_WRITE'].indexOf(access) === -1 || hidden;
  const currentAccessToken = view.accessTokens?.find(
    token => token.type === 'PUBLIC'
  );

  const hasAdminSharePrivileges = useMemo(
    () =>
      viewer != null &&
      (view.user.id === viewer.id ||
        (viewer.admin && viewerUsingAdminPrivileges()) ||
        userIsTeamAdmin),
    [view, viewer, userIsTeamAdmin]
  );

  const canChangeSharingSettings = useMemo(
    () => isOwnView(viewer, view) || hasAdminSharePrivileges,
    [viewer, view, hasAdminSharePrivileges]
  );

  const onClick = useCallback(() => {
    setIsModalOpen(true);
    onTriggerContentClick?.();
    window.analytics?.track?.('Share Report Clicked', {
      reportId: view.cid,
    });
    let projectPrivacy = '';
    if (isTeam) {
      if (isPrivateAccess) {
        projectPrivacy = 'team: private';
      } else {
        projectPrivacy = 'team: public';
      }
    } else {
      if (isPrivateAccess) {
        projectPrivacy = 'personal: private';
      } else {
        projectPrivacy = 'personal: public';
      }
    }
    let viewerDescription = '';
    if (isOwnView(viewer, view)) {
      viewerDescription = 'author';
    } else if (canChangeSharingSettings) {
      viewerDescription = 'team admin';
    } else if (isTeam) {
      if (userIsMemberOfTeam) {
        viewerDescription = 'team member';
      } else {
        viewerDescription = 'view only';
      }
    }
    window.analytics?.track('Share Report Modal Viewed', {
      reportId: view.cid,
      projectPrivacy,
      viewer: viewerDescription || 'other',
    });
  }, [
    onTriggerContentClick,
    isTeam,
    isPrivateAccess,
    viewer,
    view,
    canChangeSharingSettings,
    userIsMemberOfTeam,
  ]);

  // TODO - Move away from .less and inline style
  const triggerContent = (
    <div
      style={{display: 'contents'}}
      // Use the the capture phase b/c bubble is brittle and can be interrupted
      onClickCapture={onClick}
      onKeyDownCapture={({code}) => {
        if (['Enter', 'Space'].includes(code)) {
          onClick();
        }
      }}>
      {children}
    </div>
  );

  // PREVIEW LINK LOGIC
  const isMounted = useIsMounted();

  const encodedPathname = encodeURI(
    reportView({
      entityName: view.project?.entityName ?? '',
      projectName: view.project?.name ?? '',
      reportID: view.id ?? '',
      reportName: view.displayName,
    })
  );

  // This addresses HOSTs that have subpaths such as myinstallation.mycompany.com/wandb
  // Manually set origin for previewable links to work in local dev
  const url = urlPrefixed(encodedPathname);
  const accessTokenQS =
    currentAccessToken != null
      ? `?accessToken=${currentAccessToken.token}`
      : '';

  const magicLinkURL = `${url}${accessTokenQS}`;
  const normalLinkURL = url;

  const viewPart = useSelector(state => getReportPart(viewRef)(state));

  const {data: previewFriendlyURLData, refetch: refetchPreviewFriendlyURL} =
    useFetchPreviewableLinkQuery({
      variables: {
        resourceID: view.id ?? '',
        resourceType: PreviewableLinkResourceType.View,
      },
      context: propagateErrorsContext(),
    });

  const [
    isSurreptitiouslyCreatingAndRevokingToken,
    setIsSurreptitiouslyCreatingAndRevokingToken,
  ] = useState(false);
  const [didAttemptCreatePFURLSinceMount, setDidAttemptCreatePFURLSinceMount] =
    useState(false);

  const currentPreviewFriendlyURL =
    previewFriendlyURLData?.previewableLink?.URL;

  const firstPanelExportableAsImage =
    firstPanelExportableAsImageFromViewPart(viewPart);

  const userProvidedPreviewImageURL = view.previewUrl;

  const {deactivateLink, createMagicLink} = useMagicLinkFunctions({
    viewRef,
    view,
    currentAccessToken: currentAccessToken?.token,
    didAttemptCreatePFURLSinceMount,
    firstPanelExportableAsImage,
    previewFriendlyURLData,
    refetchPreviewFriendlyURL,
    setDidAttemptCreatePFURLSinceMount,
    userProvidedPreviewImageURL,
    encodedPathname,
    viewPart,
    isMounted,
  });

  const isSharingLinkEnabled =
    currentAccessToken != null &&
    currentAccessToken.revokedAt == null &&
    !isSurreptitiouslyCreatingAndRevokingToken;

  const useMaybeSurreptitiouslyCreatePreviewFriendlyURLProps = {
    firstPanelExportableAsImage,
    origin,
    encodedPathname,
    view,
    reportAuthors: viewPart.authors ?? [],
    isMounted,
    refetchPreviewFriendlyURL,
    userIsMemberOfTeam: userIsMemberOfTeam ?? false,
    viewLastUpdatedAt: view.updatedAt,
    fetchPreviewFriendlyURLData: previewFriendlyURLData,

    // these set of args are for creating magic link pfurls
    currentAccessToken: isPrivateAccess ? currentAccessToken?.token : undefined,
    shouldCreateAndRevokeAccessTokenIfNotExists: isPrivateAccess,
    setDidAttemptCreatePFURLSinceMount,
    createMagicLink,
    deactivateLink,
    setIsSurreptitiouslyCreatingAndRevokingToken,
  };

  const [previewURL, setPreviewURL] = useState<string | undefined>(undefined);
  const [selectedNavTab, setSelectedNavTab] = useState<string>(
    shouldShowInviteTab ? 'invite' : 'share'
  );

  useEffect(() => {
    // this refetchs the preview image, everytime the preview url changes
    if (currentPreviewFriendlyURL) {
      getPreviewImageFromURL(currentPreviewFriendlyURL, setPreviewURL).catch(
        console.error
      );
    }
  }, [currentPreviewFriendlyURL, setPreviewURL]);

  const handleToggleMagicLink = async () => {
    if (!canChangeSharingSettings) {
      return;
    }
    if (currentAccessToken != null) {
      await deactivateLink();
    } else {
      await createMagicLink();
    }
  };

  const project = accessQuery.data?.project;

  if (view.project == null) {
    return null;
  }
  if (accessQuery.loading) {
    return triggerContent;
  }

  return (
    <ShareReportTriggerContextProvider
      access={access}
      currentAccessToken={currentAccessToken}
      isPrivateAccess={isPrivateAccess}
      hidden={hidden}
      userIsMemberOfTeam={userIsMemberOfTeam}
      userIsTeamAdmin={userIsTeamAdmin}
      view={view}
      viewRef={viewRef}
      viewer={viewer}
      disableMagicLinkSharing={disableMagicLinkSharing}>
      {triggerContent}
      <S.Modal
        data-test="share-report-modal"
        id="share-report-modal"
        size="tiny"
        open={isModalOpen}
        onClose={closeModal}>
        <S.CloseIcon onClick={closeModal} />
        <S.ModalContent>
          <Tabs.Root value={selectedNavTab} onValueChange={setSelectedNavTab}>
            <Tabs.List>
              {shouldShowInviteTab && (
                <Tabs.Trigger value="invite">
                  <Icon name="share-with" />
                  Invite
                </Tabs.Trigger>
              )}
              {(!disableMagicLinkSharing ||
                (userIsMemberOfTeam &&
                  project &&
                  accessIsPublicOrOpen(project.access))) && (
                <Tabs.Trigger value="share">
                  <Icon name="share-export" />
                  Share
                </Tabs.Trigger>
              )}
            </Tabs.List>
            <Tabs.Content value="invite">
              <S.EmailScrollContent id="share-modal-user-email-scroll-content">
                <ShareInputSection
                  setShouldDeleteView={setShouldDeleteView}
                  previewURL={previewURL ?? ''}
                />
                <ShareWithSection isTeam={isTeam} />
              </S.EmailScrollContent>
            </Tabs.Content>
            <Tabs.Content value="share">
              <S.ScrollContent>
                <PreviewImageSection previewURL={previewURL} view={view} />
                {(isSharingLinkEnabled || !isPrivateAccess) && (
                  <ShareLinks
                    currentPreviewFriendlyURL={currentPreviewFriendlyURL}
                    magicLinkURL={magicLinkURL}
                    normalLinkURL={normalLinkURL}
                    reportId={view.cid}
                    setShouldDeleteView={setShouldDeleteView}
                  />
                )}
              </S.ScrollContent>
            </Tabs.Content>
          </Tabs.Root>
        </S.ModalContent>
        <CopyLinkSection
          currentPreviewFriendlyURL={currentPreviewFriendlyURL}
          magicLinkURL={magicLinkURL}
          normalLinkURL={normalLinkURL}
          handleToggleMagicLink={handleToggleMagicLink}
          isSharingLinkEnabled={isSharingLinkEnabled}
          useMaybeSurreptitiouslyCreatePreviewFriendlyURLProps={
            useMaybeSurreptitiouslyCreatePreviewFriendlyURLProps
          }
          selectedNavTab={selectedNavTab}
          setShouldDeleteView={setShouldDeleteView}
        />
      </S.Modal>
    </ShareReportTriggerContextProvider>
  );
};
